import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AlertTriangle, TrendingUp, Clock, Euro, ArrowRight, CheckCircle2, XCircle } from 'lucide-react';
import { motion } from 'framer-motion';

const Simulateur = () => {
  const navigate = useNavigate();
  
  // État du formulaire
  const [montantTravaux, setMontantTravaux] = useState('50000');
  const [tauxImposition, setTauxImposition] = useState('25');
  const [typeBien, setTypeBien] = useState('principale');
  const [augmentationValeur, setAugmentationValeur] = useState('15');
  const [valeurBien, setValeurBien] = useState('750000');
  
  // État des résultats
  const [resultats, setResultats] = useState(null);
  
  // Compte à rebours jusqu'au 01.01.2028
  const [countdown, setCountdown] = useState({ jours: 0, heures: 0, minutes: 0 });
  
  useEffect(() => {
    const targetDate = new Date('2028-01-01T00:00:00');
    
    const updateCountdown = () => {
      const now = new Date();
      const diff = targetDate - now;
      
      if (diff > 0) {
        const jours = Math.floor(diff / (1000 * 60 * 60 * 24));
        const heures = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
        
        setCountdown({ jours, heures, minutes });
      }
    };
    
    updateCountdown();
    const interval = setInterval(updateCountdown, 60000); // Update chaque minute
    
    return () => clearInterval(interval);
  }, []);
  
  const calculerROI = () => {
    const travaux = parseFloat(montantTravaux) || 0;
    const taux = parseFloat(tauxImposition) / 100 || 0.25;
    const augmentation = parseFloat(augmentationValeur) / 100 || 0.15;
    const valeur = parseFloat(valeurBien) || 0;
    
    // Économie fiscale actuelle (déductibilité à 100% en Suisse)
    const economieFiscale = travaux * taux;
    
    // Coût réel aujourd'hui
    const coutReelAujourdhui = travaux - economieFiscale;
    
    // Coût après réforme 2028 (aucune déduction)
    const coutApresReforme = travaux;
    
    // Perte si attente
    const perteSiAttente = economieFiscale;
    
    // Valorisation du bien
    const valorisation = valeur * augmentation;
    
    // Gain net potentiel
    const gainNet = valorisation - coutReelAujourdhui;
    
    setResultats({
      economieFiscale,
      coutReelAujourdhui,
      coutApresReforme,
      perteSiAttente,
      valorisation,
      gainNet
    });
  };
  
  useEffect(() => {
    calculerROI();
  }, [montantTravaux, tauxImposition, typeBien, augmentationValeur, valeurBien]);
  
  const formatCHF = (montant) => {
    return new Intl.NumberFormat('fr-CH', {
      style: 'currency',
      currency: 'CHF',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(montant);
  };
  
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Hero Section avec Compte à Rebours */}
      <div className="bg-gradient-to-r from-[#2B2B2B] to-[#1a1a1a] text-white py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="inline-flex items-center gap-2 bg-red-600/20 border border-red-600/30 rounded-full px-4 py-2 mb-6">
              <AlertTriangle className="w-4 h-4 text-red-400" />
              <span className="text-sm font-medium text-red-400">Réforme fiscale votée — Entrée en vigueur 2028</span>
            </div>
            
            <h1 
              className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6 leading-tight"
              style={{ fontFamily: 'Space Grotesk, sans-serif' }}
            >
              Calculez combien vous perdez<br />
              <span className="text-[#336DBF]">en attendant</span>
            </h1>
            
            <p className="text-lg sm:text-xl text-slate-300 mb-8 max-w-3xl mx-auto">
              Une réforme fiscale majeure a été votée en Suisse.<br />
              <strong className="text-white">Aujourd'hui, l'État participe à vos travaux.</strong><br />
              À partir de 2028, ce ne sera plus le cas.
            </p>
            
            {/* Compte à rebours */}
            <div className="flex justify-center gap-4 sm:gap-6 mb-8">
              <div className="text-center">
                <div className="bg-white/10 backdrop-blur-sm rounded-lg px-4 sm:px-6 py-3 sm:py-4 border border-white/20">
                  <div className="text-3xl sm:text-4xl font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                    {countdown.jours}
                  </div>
                  <div className="text-xs sm:text-sm text-slate-400 mt-1">jours</div>
                </div>
              </div>
              <div className="text-center">
                <div className="bg-white/10 backdrop-blur-sm rounded-lg px-4 sm:px-6 py-3 sm:py-4 border border-white/20">
                  <div className="text-3xl sm:text-4xl font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                    {countdown.heures}
                  </div>
                  <div className="text-xs sm:text-sm text-slate-400 mt-1">heures</div>
                </div>
              </div>
              <div className="text-center">
                <div className="bg-white/10 backdrop-blur-sm rounded-lg px-4 sm:px-6 py-3 sm:py-4 border border-white/20">
                  <div className="text-3xl sm:text-4xl font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                    {countdown.minutes}
                  </div>
                  <div className="text-xs sm:text-sm text-slate-400 mt-1">minutes</div>
                </div>
              </div>
            </div>
            
            <p className="text-sm text-slate-400">
              Temps restant avant l'entrée en vigueur estimée de la réforme
            </p>
          </motion.div>
        </div>
      </div>
      
      {/* Simulateur */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12">
          {/* Formulaire */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <Card className="p-6 sm:p-8 shadow-xl border-slate-200">
              <h2 className="text-2xl font-bold text-[#2B2B2B] mb-6" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                Vos informations
              </h2>
              
              <div className="space-y-6">
                {/* Montant des travaux */}
                <div>
                  <Label htmlFor="montant" className="text-base font-medium text-slate-700">
                    Montant estimé de vos travaux (CHF) *
                  </Label>
                  <Input
                    id="montant"
                    type="number"
                    value={montantTravaux}
                    onChange={(e) => setMontantTravaux(e.target.value)}
                    placeholder="50000"
                    className="mt-2 text-lg"
                  />
                  <p className="text-sm text-slate-500 mt-1">
                    Budget approximatif de votre projet de rénovation
                  </p>
                </div>
                
                {/* Valeur du bien */}
                <div>
                  <Label htmlFor="valeur" className="text-base font-medium text-slate-700">
                    Valeur actuelle de votre bien (CHF) *
                  </Label>
                  <Input
                    id="valeur"
                    type="number"
                    value={valeurBien}
                    onChange={(e) => setValeurBien(e.target.value)}
                    placeholder="750000"
                    className="mt-2 text-lg"
                  />
                </div>
                
                {/* Taux d'imposition */}
                <div>
                  <Label htmlFor="taux" className="text-base font-medium text-slate-700">
                    Votre taux marginal d'imposition (%)
                  </Label>
                  <Input
                    id="taux"
                    type="number"
                    value={tauxImposition}
                    onChange={(e) => setTauxImposition(e.target.value)}
                    placeholder="25"
                    className="mt-2 text-lg"
                  />
                  <p className="text-sm text-slate-500 mt-1">
                    Si vous ne savez pas, laissez 25% (moyenne Suisse romande)
                  </p>
                </div>
                
                {/* Type de bien */}
                <div>
                  <Label className="text-base font-medium text-slate-700">
                    Type de bien
                  </Label>
                  <Select value={typeBien} onValueChange={setTypeBien}>
                    <SelectTrigger className="mt-2">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="principale">Résidence principale</SelectItem>
                      <SelectItem value="locatif">Bien locatif</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Augmentation de valeur */}
                <div>
                  <Label htmlFor="augmentation" className="text-base font-medium text-slate-700">
                    Augmentation de valeur estimée (%)
                  </Label>
                  <Input
                    id="augmentation"
                    type="number"
                    value={augmentationValeur}
                    onChange={(e) => setAugmentationValeur(e.target.value)}
                    placeholder="15"
                    className="mt-2 text-lg"
                  />
                  <p className="text-sm text-slate-500 mt-1">
                    Valorisation attendue après rénovation complète
                  </p>
                </div>
              </div>
            </Card>
          </motion.div>
          
          {/* Résultats */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="space-y-6"
          >
            {resultats && (
              <>
                {/* Aujourd'hui vs Après 2028 */}
                <Card className="p-6 sm:p-8 shadow-xl border-green-200 bg-gradient-to-br from-green-50 to-white">
                  <div className="flex items-center gap-3 mb-4">
                    <CheckCircle2 className="w-6 h-6 text-green-600 flex-shrink-0" />
                    <h3 className="text-xl font-bold text-green-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                      Aujourd'hui (avec déduction)
                    </h3>
                  </div>
                  
                  <div className="space-y-3">
                    <div className="flex justify-between items-baseline">
                      <span className="text-slate-600">Montant travaux</span>
                      <span className="text-lg font-medium text-slate-900">{formatCHF(parseFloat(montantTravaux))}</span>
                    </div>
                    <div className="flex justify-between items-baseline">
                      <span className="text-green-700 font-medium">Économie fiscale</span>
                      <span className="text-xl font-bold text-green-700">- {formatCHF(resultats.economieFiscale)}</span>
                    </div>
                    <div className="border-t border-green-200 pt-3 flex justify-between items-baseline">
                      <span className="text-slate-900 font-bold">Coût réel</span>
                      <span className="text-2xl font-bold text-green-700">{formatCHF(resultats.coutReelAujourdhui)}</span>
                    </div>
                  </div>
                </Card>
                
                <Card className="p-6 sm:p-8 shadow-xl border-red-200 bg-gradient-to-br from-red-50 to-white">
                  <div className="flex items-center gap-3 mb-4">
                    <XCircle className="w-6 h-6 text-red-600 flex-shrink-0" />
                    <h3 className="text-xl font-bold text-red-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                      Après 2028 (sans déduction)
                    </h3>
                  </div>
                  
                  <div className="space-y-3">
                    <div className="flex justify-between items-baseline">
                      <span className="text-slate-600">Montant travaux</span>
                      <span className="text-lg font-medium text-slate-900">{formatCHF(parseFloat(montantTravaux))}</span>
                    </div>
                    <div className="flex justify-between items-baseline">
                      <span className="text-red-700 font-medium">Économie fiscale</span>
                      <span className="text-xl font-bold text-red-700">0 CHF</span>
                    </div>
                    <div className="border-t border-red-200 pt-3 flex justify-between items-baseline">
                      <span className="text-slate-900 font-bold">Coût réel</span>
                      <span className="text-2xl font-bold text-red-700">{formatCHF(resultats.coutApresReforme)}</span>
                    </div>
                  </div>
                </Card>
                
                {/* Perte si attente */}
                <Card className="p-6 sm:p-8 shadow-2xl border-2 border-[#336DBF] bg-gradient-to-br from-blue-50 to-white">
                  <div className="flex items-center gap-3 mb-4">
                    <AlertTriangle className="w-7 h-7 text-[#336DBF] flex-shrink-0" />
                    <h3 className="text-2xl font-bold text-[#2B2B2B]" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                      Perte si vous attendez
                    </h3>
                  </div>
                  
                  <div className="bg-white rounded-lg p-6 mb-4 border border-slate-200">
                    <div className="text-center">
                      <p className="text-sm text-slate-600 mb-2">Argent perdu en attendant après 2028</p>
                      <p className="text-5xl font-bold text-red-600 mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                        {formatCHF(resultats.perteSiAttente)}
                      </p>
                      <p className="text-sm text-slate-500">
                        = l'économie fiscale que vous ne ferez plus
                      </p>
                    </div>
                  </div>
                  
                  <div className="space-y-3">
                    <div className="flex justify-between items-baseline">
                      <span className="text-slate-700 font-medium">Valorisation du bien</span>
                      <span className="text-xl font-bold text-green-600">+ {formatCHF(resultats.valorisation)}</span>
                    </div>
                    <div className="border-t border-slate-200 pt-3 flex justify-between items-baseline">
                      <span className="text-slate-900 font-bold">Gain net potentiel</span>
                      <span className="text-2xl font-bold text-[#336DBF]">{formatCHF(resultats.gainNet)}</span>
                    </div>
                  </div>
                </Card>
                
                {/* Interprétation */}
                <Card className="p-6 sm:p-8 bg-[#2B2B2B] text-white border-none">
                  <h3 className="text-xl font-bold mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                    Ce que cela signifie
                  </h3>
                  
                  <div className="space-y-4 text-slate-300 leading-relaxed">
                    <p>
                      <strong className="text-white">En réalisant vos travaux aujourd'hui</strong>, vous économisez environ <strong className="text-green-400">{formatCHF(resultats.economieFiscale)}</strong> d'impôts.
                    </p>
                    
                    <p>
                      <strong className="text-white">Si cette déduction disparaît après 2028</strong>, ces <strong className="text-red-400">{formatCHF(resultats.economieFiscale)}</strong> deviennent une <strong className="text-red-400">perte directe</strong>.
                    </p>
                    
                    <p>
                      <strong className="text-white">Autrement dit :</strong> attendre vous coûte déjà de l'argent.
                    </p>
                    
                    <p>
                      En parallèle, votre bien pourrait prendre environ <strong className="text-green-400">{formatCHF(resultats.valorisation)}</strong> de valeur.
                    </p>
                    
                    <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 mt-6 border border-white/20">
                      <p className="text-white font-bold text-lg">
                        ⏳ Conclusion : Vous avez actuellement une fenêtre où l'État participe indirectement à votre rénovation.
                      </p>
                      <p className="text-[#336DBF] font-bold text-lg mt-2">
                        Cette fenêtre se ferme au 1er janvier 2028.
                      </p>
                    </div>
                  </div>
                </Card>
                
                {/* CTA Final */}
                <Card className="p-8 bg-gradient-to-r from-[#336DBF] to-[#2F62AA] text-white border-none shadow-2xl">
                  <h3 className="text-2xl sm:text-3xl font-bold mb-4 text-center" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                    Ne laissez pas {formatCHF(resultats.perteSiAttente)} sur la table
                  </h3>
                  
                  <p className="text-center text-blue-100 mb-6 text-lg">
                    Faites analyser votre projet gratuitement et voyez comment optimiser vos travaux avant la fin de cet avantage.
                  </p>
                  
                  <Button
                    onClick={() => navigate('/devis')}
                    size="lg"
                    className="w-full bg-white text-[#336DBF] hover:bg-slate-100 font-bold text-lg py-6 shadow-xl"
                  >
                    Obtenir mon devis gratuit
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                  
                  <p className="text-center text-blue-200 text-sm mt-4">
                    ✓ Réponse sous 48h • ✓ Sans engagement • ✓ Analyse fiscale incluse
                  </p>
                </Card>
                
                {/* Disclaimer légal */}
                <Card className="p-4 bg-slate-50 border-slate-200">
                  <p className="text-xs text-slate-600 text-center leading-relaxed">
                    <strong>Disclaimer :</strong> Ce simulateur est basé sur la réforme fiscale votée le 28 septembre 2025 
                    (acceptée à 57,7%) et l'entrée en vigueur estimée au 1er janvier 2028 selon le calendrier fédéral. 
                    Les montants affichés sont des estimations. La déductibilité exacte et les modalités d'application 
                    peuvent varier selon votre canton et situation personnelle. Consultez votre conseiller fiscal pour 
                    une analyse détaillée.
                  </p>
                </Card>
              </>
            )}
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default Simulateur;
